import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Text, tokens } from '@/design';

const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/v1/decoders/';

type PreviewStatus = 'loading' | 'ready' | 'empty' | 'error';

interface LayerPreviewModelProps {
  readonly modelUrl: string;
  readonly height?: number;
}

/**
 * Live 3D turntable preview of a GLB/glTF model. Companion to `LayerPreview3D`
 * (tilesets). Loads the model with meshopt + Draco decoders (matching the main
 * Giro3D bim-gltf path), auto-frames it, and rotates slowly. Self-contained
 * WebGL context, disposed on unmount.
 */
export function LayerPreviewModel({ modelUrl, height = 240 }: LayerPreviewModelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<PreviewStatus>('loading');
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !modelUrl) return;

    let disposed = false;
    let raf = 0;
    let renderer: THREE.WebGLRenderer | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const start = async () => {
      const initialW = container.clientWidth || 320;
      const initialH = container.clientHeight || height;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      } catch {
        if (!disposed) setStatus('error');
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(initialW, initialH, false);
      const canvas = renderer.domElement;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      container.appendChild(canvas);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(tokens.color.surface[0]);
      const camera = new THREE.PerspectiveCamera(50, initialW / initialH, 0.01, 1e9);
      camera.position.set(0, 0, 10);

      scene.add(new THREE.AmbientLight(0xffffff, 1.4));
      const key = new THREE.DirectionalLight(0xffffff, 1.8);
      key.position.set(1, 1.4, 1);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xa9c4ff, 0.7);
      fill.position.set(-1, 0.4, -0.8);
      scene.add(fill);

      try {
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        if (disposed) return;

        const loader = new GLTFLoader();
        try {
          const [{ MeshoptDecoder }, { DRACOLoader }] = await Promise.all([
            import('three/examples/jsm/libs/meshopt_decoder.module.js'),
            import('three/examples/jsm/loaders/DRACOLoader.js'),
          ]);
          loader.setMeshoptDecoder(MeshoptDecoder as never);
          const dracoLoader = new DRACOLoader();
          dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
          loader.setDRACOLoader(dracoLoader);
        } catch {
          /* decoders optional */
        }

        const gltf = await loader.loadAsync(modelUrl);
        if (disposed) return;

        const model = gltf.scene;
        scene.add(model);

        const box = new THREE.Box3().setFromObject(model);
        if (box.isEmpty()) {
          if (!disposed) { setDetail('Model has no visible geometry'); setStatus('empty'); }
          return;
        }

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const radius = size.length() / 2;

        model.position.sub(center);

        camera.near = Math.max(radius / 1000, 0.001);
        camera.far = radius * 100;
        camera.updateProjectionMatrix();

        if (!disposed) setStatus('ready');

        let theta = 0.7;
        const phi = 0.62;
        const animate = () => {
          if (disposed || !renderer) return;
          raf = requestAnimationFrame(animate);
          theta += 0.0042;
          const dist = radius * 2.6;
          camera.position.set(
            dist * Math.cos(phi) * Math.cos(theta),
            dist * Math.sin(phi),
            dist * Math.cos(phi) * Math.sin(theta),
          );
          camera.lookAt(0, 0, 0);
          renderer.render(scene, camera);
        };
        animate();
      } catch (err) {
        if (!disposed) {
          setDetail(err instanceof Error ? err.message : 'Failed to load model');
          setStatus('error');
        }
      }

      resizeObserver = new ResizeObserver(() => {
        if (disposed || !renderer) return;
        const w = container.clientWidth || initialW;
        const h = container.clientHeight || initialH;
        renderer.setSize(w, h, false);
        camera.aspect = w / Math.max(h, 1);
        camera.updateProjectionMatrix();
      });
      resizeObserver.observe(container);
    };

    void start();

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      if (renderer) {
        try { renderer.dispose(); renderer.forceContextLoss(); } catch { /* ignore */ }
        const canvas = renderer.domElement;
        if (canvas.parentElement === container) container.removeChild(canvas);
      }
    };
  }, [modelUrl, height]);

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: tokens.radius.md,
          overflow: 'hidden',
          border: tokens.border.thin,
          background: tokens.color.surface[0],
        }}
      >
        {status !== 'ready' ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: tokens.space[3],
              pointerEvents: 'none',
            }}
          >
            <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
              {status === 'loading'
                ? 'Loading 3D model…'
                : status === 'empty'
                  ? (detail ?? 'Model has no geometry to preview.')
                  : `3D model preview failed${detail ? ` (${detail})` : ''}.`}
            </Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}
